import { FormEventHandler, useState } from 'react'
import { useQuery } from '../utils/useQuery'
import FullScreenLoader from '../components/FullScreenLoader'
import { useEffect } from 'react'
import { fetcher } from '../utils/fetcher'
import Input from '../components/Input'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import { useMemo } from 'react'
import Checkbox from '../components/Checkbox'
import { Title } from 'react-head'
import {
  ChangeEvent,
  CustomerUserBM,
  LucideIconName,
} from '@bm-js/atlascloud-shared'

const initialFormData = {
  fullName: '',
  email: '',
  password: '',
  repeatPassword: '',
}

const fieldsConfig = [
  {
    key: 'fullName',
    label: 'Namn',
    desc: 'För- och efternamn',
    type: 'text',
  },
  {
    key: 'email',
    label: 'Mailadress',
    desc: 'Mailadressen går inte att ändra',
    disabled: true,
    type: 'email',
  },
  {
    key: 'password',
    label: 'Lösenord',
    desc: 'Välj ditt lösenord',
    type: 'password',
  },
  {
    key: 'repeatPassword',
    label: 'Upprepa lösenord',
    type: 'password',
  },
]

export type CustomerUserLoginResponse = CustomerUserBM & {
  siteUrl: string
  token: string
  websiteId: string
}

const CustomerUserRegister = () => {
  const token = useQuery().get('t')

  const [formData, setFormData] = useState(initialFormData)
  const [mounting, setMounting] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [tokenData, setTokenData] = useState<CustomerUserLoginResponse>()
  const [loading, setLoading] = useState(false)
  const [checkedBox, setCheckedBox] = useState(false)

  const handleChange = (e: ChangeEvent) => {
    setErrorMessage('')
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit: FormEventHandler = async (e) => {
    setErrorMessage('')
    e.preventDefault()
    setLoading(true)
    const { err } = await fetcher({
      path: 'customer-users-for-users/register',
      method: 'POST',
      auth: token,
      body: formData,
    })

    if (err) {
      if (typeof err === 'string') setErrorMessage(err)
      else setErrorMessage('Något gick fel. Försök igen senare.')
      setLoading(false)
      return
    }

    setSuccessMessage('Kontot skapades! Omdirigerar...')

    const { err: loginErr, data } = await fetcher<CustomerUserLoginResponse>({
      path: `customer-users-for-users/login`,
      method: 'POST',
      body: {
        email: formData.email,
        password: formData.password,
        websiteId: tokenData?.websiteId,
      },
    })

    if (loginErr || !data) {
      setTimeout(() => {
        setErrorMessage('Kunde inte omdirigera')
      }, 300)
      return
    }
    const redirectUrl = `https://${data.siteUrl}?t=${data.token}`
    setTimeout(() => {
      window.location.href = redirectUrl
    }, 300)
    setLoading(false)
  }

  const handleMount = async () => {
    if (!token) return setMounting(false)

    const { err, data } = await fetcher<CustomerUserLoginResponse>({
      path: 'customer-users-for-users/verify-register-token',
      auth: token,
    })

    if (err || !data) return setMounting(false)
    setTokenData(data)
    setFormData({
      ...initialFormData,
      email: data.email,
      fullName: data.fullName as string,
    })
    setMounting(false)
  }

  const passwordRequirements = useMemo(
    () => [
      {
        desc: 'Minst 8 tecken',
        key: '8chars',
        check: (p: string) => p.length >= 8,
      },
      {
        desc: 'Minst en stor bokstav',
        key: 'oneCapital',
        check: (p: string) => /.*[A-Z].*/.test(p),
      },
      {
        desc: 'Minst en liten bokstav',
        key: 'oneLower',
        check: (p: string) => /.*[a-z].*/.test(p),
      },
      {
        desc: 'Minst en siffra',
        key: 'oneNumber',
        check: (p: string) => /.*[0-9].*/.test(p),
      },
      {
        desc: 'Lösenorden är lika',
        key: 'samePass',
        check: (p: string, p2?: string) => !!p && !!p2 && p === p2,
      },
    ],
    []
  )

  const passwordFullfillment = useMemo<Record<string, string>>(() => {
    return passwordRequirements.reduce((acc, curr) => {
      // @ts-expect-error tmp
      acc[curr.key] = curr.check(formData.password, formData.repeatPassword)
      return acc
    }, {})
  }, [formData.password, passwordRequirements, formData.repeatPassword])

  const submitDisabled = useMemo(() => {
    if (loading) return true
    if (
      Object.keys(passwordFullfillment).some((k) => !passwordFullfillment[k])
    ) {
      return true
    }
    if (!checkedBox) return true
    return false
  }, [passwordFullfillment, loading, checkedBox])

  useEffect(() => {
    handleMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const boxStyles = 'rounded-md bg-white p-4 shadow-xl'

  if (mounting) return <FullScreenLoader />
  if (!token || !tokenData) return <InvalidLink />
  return (
    <>
      <Title>Skapa konto | Atlascloud</Title>
      <div className='flex h-screen w-screen items-center justify-center bg-primary-400/40 p-4'>
        <div className='flex w-full flex-col items-start gap-4 md:w-[unset] md:flex-row'>
          <form
            onSubmit={handleSubmit}
            className={`w-full min-w-[unset] md:w-[unset] md:min-w-[400px] ${boxStyles}`}
          >
            <p className='mb-4 text-2xl font-bold text-primary-400'>
              Skapa ditt konto
            </p>
            {fieldsConfig.map(({ key, ...field }) => (
              <Input
                {...field}
                key={key}
                value={formData[key as keyof typeof formData]}
                onChange={handleChange}
                name={key}
                disabled={loading || field.disabled}
                required
              />
            ))}
            <Checkbox
              checked={checkedBox}
              onChange={(e) => setCheckedBox(e.target.checked)}
              name='accept'
              secondPlaceholder={
                'Jag accepterar att namn och mailadress sparas.'
              }
            />
            <Button className='w-full' type='submit' disabled={submitDisabled}>
              {loading ? 'Skapar konto...' : 'Skapa konto'}
            </Button>
            {errorMessage && (
              <p className=' mx-auto mt-2 max-w-[300px] text-center font-bold text-red-500'>
                {errorMessage}
              </p>
            )}
            {successMessage && (
              <p className=' mx-auto mt-2 max-w-[300px] text-center font-bold text-green-700'>
                {successMessage}
              </p>
            )}
          </form>
          <div className={`${boxStyles} w-full md:w-[unset] md:min-w-[300px]`}>
            <p className='mb-4 text-lg font-bold text-primary-400'>
              Lösenordskrav
            </p>
            <ul>
              {passwordRequirements.map((req) => (
                <li key={req.key} className={`flex items-center gap-2`}>
                  <Icon
                    strokeWidth={1.5}
                    size={22}
                    name={
                      passwordFullfillment[req.key]
                        ? LucideIconName.CircleCheck
                        : LucideIconName.CircleX
                    }
                    className={
                      passwordFullfillment[req.key]
                        ? 'text-green-500'
                        : 'text-red-500'
                    }
                  />
                  <span>{req.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}

export default CustomerUserRegister

const InvalidLink = () => {
  return (
    <div className='grid h-screen w-screen place-content-center'>
      <div className='max-w-[400px] text-center'>
        <h1 className='mb-4 text-primary-400'>Felaktig länk</h1>
        <p>
          Länken du gått in på är inte giltig. <br />
          Detta kan bero på att länkens giltighetstid har gått ut.{' '}
          <a href='mailto:info@bucketmedia.se' className='underline'>
            Kontakta oss
          </a>{' '}
          för att få en ny länk.
        </p>
      </div>
    </div>
  )
}
